import { useMemo } from 'react';
import { FieldArray } from 'formik';
import { useTheme } from '@emotion/react';
import { useMedia, getComposedFieldName } from '@packlink/utils';
import { Currency, CustomsReasonToExport } from '@packlink/packlink-sdk';
import { useTranslation, Trans } from '@packlink/translation-provider';
import { Typography, Grid, GridChild, InlineNotification, NotificationType, FieldMessage, FieldMessageType, Button, ButtonVariant } from '@shipengine/giger';
import { InventoryItem } from './InventoryItem.js';
import { useCustomsFormContext, useCustomsFormData } from '../../../CustomsFormData.js';
import { getInventoryListStyles, getInventoryWeightNoteStyles, getInventoryAddButtonStyles } from './InventoryStyles.js';
import { jsxs, jsx, Fragment } from '@emotion/react/jsx-runtime';
import { customFormInventoryErrorTypes } from '../../../data/validations/customsFormValidations.js';
import { getNewCustomsInvoiceItem } from '../../../data/dataTransformation.js';
import { CustomsSection } from '../../Partials/CustomsSection.js';

function Inventory(_ref) {
  let {
    colSpanDesktop,
    shipmentWeight,
    contentValue,
    ...props
  } = _ref;
  const {
    t
  } = useTranslation();
  const theme = useTheme();
  const {
    fieldNamePrefix,
    defaultCustomsCategory
  } = useCustomsFormContext();
  // TODO: currency probably shouldn't be fetched from values and should be provided from props
  // https://auctane.atlassian.net/browse/PS-1446
  const {
    values: {
      inventoryOfContents,
      currency,
      reasonsToExport
    },
    errors
  } = useCustomsFormData();
  const currencySymbol = useMemo(() => Currency.getCurrencySymbol(currency), [currency]);
  const isExportDocuments = (reasonsToExport === null || reasonsToExport === void 0 ? void 0 : reasonsToExport.value) === CustomsReasonToExport.DOCUMENTS;
  const isFromTablet = useMedia(theme.mediaQuery('tablet'));
  const isMaxWeightError = errors.inventoryOfContents === customFormInventoryErrorTypes.MAX_WEIGHT_AND_VALUE_ERROR || errors.inventoryOfContents === customFormInventoryErrorTypes.MAX_WEIGHT_ERROR;
  const isMaxValueError = errors.inventoryOfContents === customFormInventoryErrorTypes.MAX_WEIGHT_AND_VALUE_ERROR || errors.inventoryOfContents === customFormInventoryErrorTypes.MAX_VALUE_ERROR;
  if (isExportDocuments) {
    return null;
  }
  return jsxs(CustomsSection, {
    title: t('inventory.header.title'),
    ...props,
    children: [jsx(Typography, {
      children: jsx(Trans, {
        i18nKey: "inventory.header.description"
      })
    }), jsx(Grid, {
      noPadding: true,
      children: jsx(GridChild, {
        colSpan: 12,
        colSpanDesktop: colSpanDesktop,
        children: jsx(FieldArray, {
          name: getComposedFieldName(fieldNamePrefix, 'inventoryOfContents'),
          render: arrayHelpers => jsxs(Fragment, {
            children: [jsx("section", {
              css: getInventoryListStyles,
              children: inventoryOfContents.map((_item, index) => jsx(InventoryItem, {
                index: index,
                onRemoveItem: () => arrayHelpers.remove(index),
                currency: currency
              }, index))
            }), jsx("div", {
              children: jsxs(InlineNotification, {
                type: NotificationType.INFO,
                children: [jsxs("strong", {
                  css: getInventoryWeightNoteStyles,
                  children: [t('customs-form.max-weight.note'), ":"]
                }), jsx(Trans, {
                  parent: "span",
                  i18nKey: "customs-form.max-weight.disclaimer",
                  values: {
                    maxWeight: shipmentWeight
                  }
                })]
              })
            }), isMaxWeightError && jsx(FieldMessage, {
              type: FieldMessageType.ERROR,
              children: t('customs-form.max-weight.disclaimer', {
                maxWeight: shipmentWeight
              })
            }), isMaxValueError && jsx(FieldMessage, {
              type: FieldMessageType.ERROR,
              children: t('customs-form.max-value.disclaimer', {
                maxValue: contentValue,
                currency: currencySymbol
              })
            }), jsx("div", {
              children: jsx(Button, {
                variant: ButtonVariant.OUTLINED,
                css: getInventoryAddButtonStyles,
                onClick: () => arrayHelpers.push(getNewCustomsInvoiceItem({
                  defaultCustomsCategory,
                  t
                })),
                isFullWidth: !isFromTablet,
                children: t('inventory.button.add-item')
              })
            })]
          })
        })
      })
    })]
  });
}

export { Inventory };
//# sourceMappingURL=Inventory.js.map
